import React, { useState, useEffect } from 'react';
import { Power, Zap, Lock, Globe, Bell, Save, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const PlatformSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    maintenance: false,
    thinkingMode: true,
    studioSync: true,
    weeklyFreeTokens: 5.00,
    weeklyProTokens: 20.00,
    freeProjectLimit: 3,
    proProjectLimit: 10,
    autoBanExploits: true
  });

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('*');
      
      if (error) throw error;
      
      const mapped = {};
      data.forEach(item => {
        mapped[item.key] = item.value;
      });

      if (Object.keys(mapped).length > 0) {
        setSettings(prev => ({ ...prev, ...mapped }));
      }
    } catch (err) {
      toast.error("Failed to load system config.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const promises = Object.entries(settings).map(([key, value]) => 
        supabase.from('system_settings').upsert({ key, value })
      );
      
      await Promise.all(promises);
      toast.success("Platform master record deployed.");
    } catch (err) {
      toast.error("Deployment failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-20 text-center"><Loader2 className="animate-spin mx-auto text-sonas-dim" /></div>;

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <section>
        <h1 className="text-2xl font-syne font-bold">System Configuration</h1>
        <p className="text-sonas-muted text-xs mt-1">Global runtime parameters and security thresholds.</p>
      </section>

      <div className="space-y-10">
        <section className="space-y-4">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-sonas-dim flex items-center gap-2 px-1">
            <Power size={12} /> Live Module Control
          </h3>
          <div className="bg-sonas-surface border border-sonas-border rounded-3xl divide-y divide-sonas-border overflow-hidden shadow-sm">
            {[
              { id: 'maintenance', label: 'Global Maintenance', desc: 'Restrict all incoming API traffic to Admin UIDs only.', icon: Lock, color: 'text-amber-500' },
              { id: 'thinkingMode', label: 'Deep Reasoning (CoT)', desc: 'Toggle access to high-compute inference models.', icon: Zap, color: 'text-sonas-accent' },
              { id: 'studioSync', label: 'Studio Synchronizer', desc: 'Allow real-time script injection into external Studio clients.', icon: Globe, color: 'text-blue-500' },
              { id: 'autoBanExploits', label: 'Auto-Filter Firewall', desc: 'Instantly ban users attempting to generate malicious vectors.', icon: ShieldCheck, color: 'text-red-500' },
            ].map(item => (
              <div key={item.id} className="p-6 flex items-center justify-between gap-6 hover:bg-sonas-surface2/30 transition-colors">
                <div className="flex gap-4">
                  <div className={cn("h-11 w-11 rounded-2xl bg-sonas-surface2 flex items-center justify-center shrink-0 border border-sonas-border", item.color)}>
                    <item.icon size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-bold">{item.label}</p>
                    <p className="text-[11px] text-sonas-muted mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
                <Switch 
                  checked={settings[item.id]} 
                  onCheckedChange={() => handleToggle(item.id)}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-sonas-dim flex items-center gap-2 px-1">
            <Bell size={12} /> Resource Allocation (Weekly)
          </h3>
          <div className="bg-sonas-surface border border-sonas-border rounded-3xl p-8 grid grid-cols-1 md:grid-cols-2 gap-8 shadow-sm">
            <div className="space-y-3">
              <label className="text-[10px] font-bold uppercase tracking-widest text-sonas-muted">Free Tokens ($)</label>
              <Input 
                type="number" step="0.01" 
                value={settings.weeklyFreeTokens}
                onChange={(e) => setSettings({...settings, weeklyFreeTokens: e.target.value})}
                className="bg-sonas-surface2 border-sonas-border h-12 rounded-xl px-4 font-mono text-sm"
              />
            </div>
            <div className="space-y-3">
              <label className="text-[10px] font-bold uppercase tracking-widest text-sonas-muted">Pro Tokens ($)</label>
              <Input 
                type="number" step="0.01" 
                value={settings.weeklyProTokens}
                onChange={(e) => setSettings({...settings, weeklyProTokens: e.target.value})}
                className="bg-sonas-surface2 border-sonas-border h-12 rounded-xl px-4 font-mono text-sm"
              />
            </div>
          </div>
        </section>

        <div className="pt-4">
          <Button onClick={handleSave} disabled={saving} className="w-full h-14 bg-sonas-text text-sonas-bg font-bold rounded-2xl shadow-xl hover:opacity-90 transition-all gap-3 text-sm">
            {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />} 
            Deploy System Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlatformSettings;